/**
 * A deliberately well-configured device. Every rule must stay silent on it.
 *
 * This is the single most important fixture in the project: a tool that cries wolf on a
 * hardened config is worse than no tool, because the engineer checks it once, sees a
 * finding he knows is wrong, and never returns. False positives are the failure mode
 * that kills credibility fastest, so the clean-config test guards against all of them
 * at once.
 */
export const CLEAN_CONFIG = `#config-version=FGT60F-7.4.4
config system global
    set hostname "FGT-HQ-01"
    set admin-sport 8443
    set admin-ssh-port 2222
    set admintimeout 5
    set admin-telnet disable
    set admin-ssh-v1 disable
    set admin-https-ssl-versions tlsv1-2 tlsv1-3
    set admin-server-cert "corp-issued-2026"
    set admin-lockout-threshold 3
    set admin-lockout-duration 300
    set admin-maintainer disable
    set pre-login-banner enable
    set post-login-banner enable
    set ssl-static-key-ciphers disable
    set strong-crypto enable
    set timezone 04
end
config system dns
    set primary 1.1.1.1
    set secondary 9.9.9.9
end
config system password-policy
    set status enable
    set min-length 14
end
config system ntp
    set ntpsync enable
    set type default
end
config log syslogd setting
    set status enable
    set server "10.10.0.50"
    set port 514
end
config system admin
    edit "netops"
        set accprofile "super_admin"
        set two-factor fortitoken
        set trusthost1 10.10.0.0 255.255.255.0
    next
end
config system interface
    edit "port1"
        set role wan
        set allowaccess ping
    next
    edit "port2"
        set role lan
        set allowaccess ping https ssh
    next
    edit "port3"
        set role dmz
        set allowaccess ping
    next
end
config firewall policy
    edit 1
        set name "LAN to internet"
        set srcintf "port2"
        set dstintf "port1"
        set srcaddr "LAN_NET"
        set dstaddr "all"
        set service "HTTPS" "DNS"
        set action accept
        set schedule "always"
        set logtraffic all
        set nat enable
        set utm-status enable
        set ips-sensor "default"
        set av-profile "default"
        set application-list "default"
    next
    edit 2
        set name "DMZ web out"
        set srcintf "port3"
        set dstintf "port1"
        set srcaddr "DMZ_WEB"
        set dstaddr "all"
        set service "HTTPS"
        set action accept
        set schedule "always"
        set logtraffic all
        set nat enable
        set utm-status enable
        set ips-sensor "default"
        set av-profile "default"
        set application-list "default"
    next
end
`

/**
 * A device with the problems this tool exists to find. Loosely modelled on what a
 * five-year-old ruleset actually looks like after a series of "just make it work" changes.
 */
export const MESSY_CONFIG = `#config-version=FGT60E-6.4.9
config system global
    set hostname "fw-old"
    set admin-telnet enable
    set admintimeout 480
    set admin-https-ssl-versions tlsv1-0 tlsv1-1 tlsv1-2
end
config system admin
    edit "admin"
        set accprofile "super_admin"
    next
    edit "contractor"
        set accprofile "super_admin"
        set trusthost1 0.0.0.0 0.0.0.0
    next
end
config system interface
    edit "wan1"
        set role wan
        set allowaccess ping https ssh http telnet
    next
    edit "internal"
        set role lan
        set allowaccess ping https ssh http
    next
end
config system snmp community
    edit 1
        set name "public"
    next
end
config firewall policy
    edit 1
        set name "temporary - remove me"
        set srcintf "internal"
        set dstintf "wan1"
        set srcaddr "all"
        set dstaddr "all"
        set service "ALL"
        set action accept
        set schedule "always"
        set logtraffic disable
    next
    edit 2
        set name "web servers out"
        set srcintf "internal"
        set dstintf "wan1"
        set srcaddr "WEB_SERVERS"
        set dstaddr "all"
        set service "HTTPS"
        set action accept
        set schedule "always"
        set logtraffic all
    next
    edit 3
        set name "old vpn rule"
        set srcintf "internal"
        set dstintf "wan1"
        set srcaddr "VPN_NET"
        set dstaddr "all"
        set service "ALL"
        set action accept
        set status disable
    next
end
`
